import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getMyFamilyGroups, createFamilyGroup, inviteFamilyMember,
  getMemberProfile, getMemberPets, updateMemberPerms, removeMember
} from '../services/familyApi';
import '../styles/PetManager.css';

const ROLE_LABELS = {
  owner:     '👑 Owner',
  parent:    '👩‍👧 Parent',
  spouse:    '💑 Spouse',
  child:     '👶 Child',
  sibling:   '👫 Sibling',
  caregiver: '🩺 Caregiver',
  other:     '👤 Member'
};

const ROLE_OPTIONS = ['parent', 'spouse', 'child', 'sibling', 'caregiver', 'other'];

const SPECIES_ICONS = {
  dog:'🐕', cat:'🐈', bird:'🐦', rabbit:'🐇',
  reptile:'🦎', fish:'🐠', small_animal:'🐹', horse:'🐴', other:'🐾'
};

// ── Member detail modal ───────────────────────────────────────
function MemberModal({ groupId, member, currentUserId, isOwner, onClose, onRefresh }) {
  const [tab, setTab]         = useState('profile');
  const [profile, setProfile] = useState(null);
  const [pets, setPets]       = useState(null);
  const [loading, setLoading] = useState(true);

  const userId = typeof member.userId === 'object' ? member.userId._id : member.userId;
  const name   = typeof member.userId === 'object'
    ? `${member.userId.firstName} ${member.userId.lastName}`
    : 'Family Member';
  const isSelf = userId === currentUserId;

  useEffect(() => {
    Promise.all([
      getMemberProfile(groupId, userId).catch(() => null),
      getMemberPets(groupId, userId).catch(() => null)
    ]).then(([profileRes, petsRes]) => {
      setProfile(profileRes?.data?.member?.profile || null);
      setPets(petsRes?.data?.pets || []);
    }).finally(() => setLoading(false));
  }, [groupId, userId]);

  const handleTogglePerm = async (key, val) => {
    try {
      await updateMemberPerms(groupId, userId, { [key]: val });
      toast.success('Permissions updated');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleRemove = async () => {
    const msg = isSelf ? 'Leave this family group?' : `Remove ${name} from the group?`;
    if (!window.confirm(msg)) return;
    try {
      await removeMember(groupId, userId);
      toast.success(isSelf ? 'You have left the group' : 'Member removed');
      onRefresh();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:20 }}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:520, maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:10 }}>
          <h3 style={{ flex:1, margin:0, fontSize:16 }}>{name}</h3>
          <span className="pm-pill grey" style={{ textTransform:'capitalize' }}>{ROLE_LABELS[member.role] || member.role}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#a0aec0' }}>✕</button>
        </div>

        <div style={{ display:'flex', borderBottom:'1px solid #e2e8f0' }}>
          {['profile', 'pets', isOwner && 'settings'].filter(Boolean).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              background:'none', border:'none', cursor:'pointer', padding:'10px 16px',
              fontSize:13, fontWeight:600, textTransform:'capitalize', fontFamily:'inherit',
              color: tab === t ? '#667eea' : '#718096',
              borderBottom: tab === t ? '2px solid #667eea' : '2px solid transparent',
              marginBottom: -1
            }}>{t}</button>
          ))}
        </div>

        <div style={{ padding:20 }}>
          {loading ? (
            <p style={{ textAlign:'center', color:'#a0aec0', padding:'2rem 0' }}>Loading…</p>
          ) : (
            <>
              {tab === 'profile' && profile && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {[
                    ['Name', `${profile.firstName} ${profile.lastName}`],
                    ['Email', profile.email],
                    ['Date of Birth', profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : '—'],
                    ['Blood Type', profile.medicalInfo?.bloodType || '—'],
                    ['Allergies', profile.medicalInfo?.allergies?.join(', ') || '—'],
                    ['Emergency Contact', profile.emergencyContact?.name || '—'],
                    ['EC Phone', profile.emergencyContact?.phone || '—'],
                  ].map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.6px', color:'#a0aec0', marginBottom:3 }}>{label}</div>
                      <div style={{ fontSize:13, color:'#2d3748' }}>{val}</div>
                    </div>
                  ))}
                  {!profile.medicalInfo && (
                    <p style={{ gridColumn:'1/-1', fontSize:13, color:'#718096', fontStyle:'italic' }}>
                      Medical information is hidden. The group owner can grant you access.
                    </p>
                  )}
                </div>
              )}

              {tab === 'pets' && (
                pets.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'2rem 0', color:'#a0aec0' }}>
                    <div style={{ fontSize:'2rem', marginBottom:8 }}>🐾</div>
                    <p style={{ margin:0 }}>No pets, or you don't have permission to view them.</p>
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {pets.map(pet => (
                      <div key={pet._id} style={{
                        display:'flex', alignItems:'center', gap:12,
                        padding:'10px 14px', background:'#f7fafc',
                        border:'1px solid #e2e8f0', borderRadius:10
                      }}>
                        <span style={{ fontSize:'1.8rem' }}>{SPECIES_ICONS[pet.medicalInfo?.species] || '🐾'}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:14, color:'#2d3748' }}>{pet.name}</div>
                          <div style={{ fontSize:12, color:'#718096', textTransform:'capitalize' }}>
                            {pet.medicalInfo?.species}{pet.medicalInfo?.breed ? ` · ${pet.medicalInfo.breed}` : ''}
                          </div>
                        </div>
                        <span className={`pm-pill ${pet.publicProfile?.enabled ? 'green' : 'grey'}`}>
                          {pet.publicProfile?.enabled ? 'Public' : 'Private'}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === 'settings' && isOwner && (
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  <p style={{ fontSize:13, color:'#718096', margin:0 }}>
                    Control what {name} can see in this family group.
                  </p>

                  {[
                    ['canViewMedical', 'Can view medical information', member.canViewMedical],
                    ['canViewPets',    'Can view pets',                member.canViewPets],
                  ].map(([key, label, val]) => (
                    <div key={key} style={{
                      display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'10px 14px', background:'#f7fafc', borderRadius:8, border:'1px solid #e2e8f0'
                    }}>
                      <span style={{ fontSize:14 }}>{label}</span>
                      <button
                        onClick={() => handleTogglePerm(key, !val)}
                        className={`pm-toggle ${val ? 'on' : ''}`}
                      >
                        <span className="pm-toggle-thumb" />
                      </button>
                    </div>
                  ))}

                  {!isSelf && member.role !== 'owner' && (
                    <button
                      style={{ marginTop:8, background:'#fff5f5', border:'1px solid #feb2b2', color:'#c53030', padding:'10px 16px', borderRadius:8, fontWeight:600, cursor:'pointer', fontFamily:'inherit', fontSize:14 }}
                      onClick={handleRemove}
                    >
                      🗑 Remove from group
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────
export default function FamilyManager({ currentUserId }) {
  const [groups, setGroups]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showInvite, setShowInvite] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [newGroupName, setNewGroupName]     = useState('');
  const [inviteForm, setInviteForm]         = useState({ email: '', role: 'other' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyFamilyGroups();
      setGroups(res.data.groups);
    } catch { toast.error('Failed to load family groups'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async e => {
    e.preventDefault();
    try {
      await createFamilyGroup(newGroupName || 'My Family');
      toast.success('Family group created!');
      setNewGroupName('');
      setShowCreate(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleInvite = async e => {
    e.preventDefault();
    if (!inviteForm.email || !showInvite) return;
    try {
      await inviteFamilyMember(showInvite, inviteForm.email, inviteForm.role);
      toast.success('Invitation sent!');
      setInviteForm({ email: '', role: 'other' });
      setShowInvite(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to invite'); }
  };

  return (
    <div className="pm-root">
      <div className="pm-header">
        <h2>Family</h2>
        <button className="pm-btn pm-btn-primary" onClick={() => setShowCreate(v => !v)}>
          {showCreate ? 'Cancel' : '+ Create Group'}
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="pm-add-form">
          <h3>Create Family Group</h3>
          <div className="pm-field">
            <label className="pm-label">Group Name</label>
            <input className="pm-input" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
              placeholder="e.g. The Smiths" maxLength={64} autoFocus />
          </div>
          <p className="pm-hint">You'll be added as the owner. You can invite family members after creating.</p>
          <button type="submit" className="pm-btn pm-btn-primary" style={{ marginTop:8 }}>Create Group</button>
        </form>
      )}

      {loading ? (
        <div className="pm-loading">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="pm-empty">
          <span className="pm-empty-icon">👨‍👩‍👧‍👦</span>
          <p>No family groups yet. Create one to share profiles and pet info with family members.</p>
        </div>
      ) : (
        groups.map(group => {
          const myMembership = group.members.find(m => {
            const uid = typeof m.userId === 'object' ? m.userId._id : m.userId;
            return uid === currentUserId;
          });
          const isOwner = myMembership?.role === 'owner';

          return (
            <div key={group._id} style={{
              background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:12,
              marginBottom:16, overflow:'hidden'
            }}>
              {/* Group header */}
              <div style={{
                padding:'14px 18px', borderBottom:'1px solid #e2e8f0',
                display:'flex', alignItems:'center', gap:10, background:'#f7fafc'
              }}>
                <span style={{ fontSize:20 }}>👨‍👩‍👧‍👦</span>
                <div style={{ flex:1 }}>
                  <h3 style={{ margin:0, fontSize:15, color:'#2d3748' }}>{group.name}</h3>
                  <p style={{ margin:0, fontSize:12, color:'#a0aec0' }}>
                    {group.members.length} member{group.members.length !== 1 ? 's' : ''}
                    {group.invitations?.filter(i => i.status === 'pending').length > 0 &&
                      ` · ${group.invitations.filter(i => i.status === 'pending').length} pending`}
                  </p>
                </div>
                <button
                  className="pm-btn pm-btn-ghost pm-btn-sm"
                  onClick={() => setShowInvite(showInvite === group._id ? null : group._id)}
                >
                  + Invite
                </button>
              </div>

              {/* Invite form */}
              {showInvite === group._id && (
                <form onSubmit={handleInvite} style={{
                  padding:'14px 18px', borderBottom:'1px solid #e2e8f0',
                  background:'#f0f4ff', display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end'
                }}>
                  <div style={{ flex:1, minWidth:180 }}>
                    <label className="pm-label">Email</label>
                    <input className="pm-input" type="email" value={inviteForm.email}
                      onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="family@example.com" required />
                  </div>
                  <div style={{ minWidth:130 }}>
                    <label className="pm-label">Relationship</label>
                    <select className="pm-input" value={inviteForm.role}
                      onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                      {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </div>
                  <button type="submit" className="pm-btn pm-btn-primary pm-btn-sm">Send Invite</button>
                </form>
              )}

              {/* Member list */}
              <div style={{ padding:'8px 10px' }}>
                {group.members.map(member => {
                  const u = typeof member.userId === 'object' ? member.userId : {};
                  const uid = u._id || member.userId;
                  const isSelf = uid === currentUserId;

                  return (
                    <div key={uid} onClick={() => setSelectedMember({ groupId: group._id, member, isOwner })}
                      style={{
                        display:'flex', alignItems:'center', gap:10, padding:'10px 10px',
                        borderRadius:8, cursor:'pointer', transition:'background .1s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f7fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{
                        width:36, height:36, borderRadius:'50%', background:'#ebf4ff',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:14, fontWeight:700, color:'#667eea', flexShrink:0
                      }}>
                        {u.firstName?.[0]}{u.lastName?.[0]}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:600, fontSize:14, color:'#2d3748' }}>
                          {u.firstName} {u.lastName}
                          {isSelf && <span style={{ fontSize:11, color:'#a0aec0', marginLeft:6 }}>(you)</span>}
                        </div>
                        <div style={{ fontSize:12, color:'#a0aec0' }}>{u.email}</div>
                      </div>
                      <span style={{ fontSize:12, color:'#718096', textTransform:'capitalize' }}>
                        {ROLE_LABELS[member.role] || member.role}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {selectedMember && (
        <MemberModal
          groupId={selectedMember.groupId}
          member={selectedMember.member}
          currentUserId={currentUserId}
          isOwner={selectedMember.isOwner}
          onClose={() => setSelectedMember(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}
